import type { BadgeTone } from '@tessma/design-system';

/**
 * THE LIFECYCLE WORDS, AND THE COLOUR EACH ONE WEARS.
 *
 * Shared because two Phase 1 screens show the same statuses: Screen 04's
 * register and Screen 01's recent-invoices card, Screen 10's register and
 * Screen 01's recent-bills card. Two copies would be two chips disagreeing
 * about the same invoice on two pages of the same module.
 *
 * NOT A TRANSLATION AND NOT A JUDGEMENT. `part_paid` and "Part-paid" are the
 * same fact; the underscore is a column name leaking into a page, which §18
 * asks us to stop doing. An unmapped status falls through VERBATIM rather than
 * being hidden or renamed, so a lifecycle the server grows later shows up as
 * itself instead of disappearing from a screen.
 *
 * `overdue` IS ABSENT FROM BOTH, DELIBERATELY. It is not a status in this
 * product — the migration says so in as many words — and no chip may claim it.
 */

const INVOICE_WORD: Readonly<Record<string, string>> = {
  draft: 'Draft',
  pending_approval: 'Pending approval',
  approved: 'Approved',
  issued: 'Issued',
  part_paid: 'Part-paid',
  paid: 'Paid',
  disputed: 'Disputed',
  void: 'Void',
};

/**
 * The chip's colour per invoice status.
 *
 * The shared `statusTone` knows `paid` and `issued` and calls everything else
 * neutral, which would draw a part-paid invoice and a disputed one the same as
 * a draft.
 */
const INVOICE_TONE: Readonly<Record<string, BadgeTone>> = {
  draft: 'neutral',
  pending_approval: 'warning',
  approved: 'info',
  issued: 'info',
  part_paid: 'warning',
  paid: 'success',
  disputed: 'danger',
  void: 'neutral',
};

const BILL_WORD: Readonly<Record<string, string>> = {
  draft: 'Draft',
  pending_approval: 'Awaiting approval',
  approved: 'Approved',
  posted: 'Posted',
  part_paid: 'Part-paid',
  paid: 'Paid',
  disputed: 'Disputed',
  void: 'Void',
};

const BILL_TONE: Readonly<Record<string, BadgeTone>> = {
  draft: 'neutral',
  pending_approval: 'warning',
  approved: 'info',
  posted: 'info',
  part_paid: 'warning',
  paid: 'success',
  disputed: 'danger',
  void: 'neutral',
};

export function invoiceStatusWord(status: string): string {
  return INVOICE_WORD[status] ?? status;
}

export function invoiceStatusTone(status: string): BadgeTone {
  return INVOICE_TONE[status] ?? 'neutral';
}

export function billStatusWord(status: string): string {
  return BILL_WORD[status] ?? status;
}

export function billStatusTone(status: string): BadgeTone {
  return BILL_TONE[status] ?? 'neutral';
}

/** Invoice statuses that still represent money the customer has not paid. */
export const LIVE_INVOICE_STATUSES: readonly string[] = ['issued', 'part_paid', 'disputed'];

/** Bill statuses that still represent money we have not paid. */
export const LIVE_BILL_STATUSES: readonly string[] = ['posted', 'part_paid', 'disputed'];
